/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    makeStyles,
} from '@material-ui/core';
import {
    arrayOf, shape, string, number, object, func, bool,
} from 'prop-types';
import { useDispatch } from 'react-redux';
import { decodeHTML } from 'entities';

import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';

import PersonalizedHomePageGridUI from '../../../GraphqlHomePage/Partials/PersonalizedHomePageGridUI/PersonalizedHomePageGridUI';
import ProductGrid from './ProductGridUI';
import PersonalizedHPCarouselBuilder from '../../../GraphqlHomePage/Partials/PersonalizedHomePageCarouselBuilder/PersonalizedHomePageCarouselBuilder';
import SimpleCarouselBuilder from '../../../EnterpriseID/Common/SimpleCarouselBuilder';
import noop from '../../../../../helpers/noop';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    oldGridRoot: {
        width: '100%',
        margin: '0 auto 60px auto',
        maxWidth: '1380px',
        // Tablet
        [theme.breakpoints.down('md')]: {
            padding: '0 20px 0',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            margin: '0 0 15px 0',
            padding: '0',
        },
    },
    oldGridTitle: {
        margin: '0 0 15px 0',
        textAlign: 'center',
        width: '100%',
        '&--title': {
            margin: '0',
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: '600',
        },
    },
}));

// 6810; this component is based off PersonalizedHomePageCarouselBuilder
const PLARecsBuilder = ({
    data,
    products,
    contentData,
    title,
    trackGA4ProductClick,
    dataTestId,
    experimentTracking,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const personalizedCarouselBlock = contentData?.personalized_carousel || {};
    const cmsTitle = contentData?.title;

    // min_products_to_show = from SF, campaign...minimumProductsToShow
    const cmsData = {
        title: cmsTitle,
        min_products_to_show: 1,
        padding: personalizedCarouselBlock?.padding,
    };
    // 6810; todo

    // 6810; this is usually assigned to a variable 'productStylingConfig' and passed as same, or productStyleConfig
    // this is the card reference if it exists, otherwise the service
    const cmsCardRef = contentData?.personalized_product_card_reference;
    const useCardDesign = cmsCardRef?.length > 0;
    const cmsCardRefData = useCardDesign ? cmsCardRef?.[0] : (contentData || {});

    // 6810; SimpleCarouselBuilder
    // name of prop = blockData
    // all data is from CMS, content.personalized_carousel
    const scbBlockObj = {
        productsToShow: contentData?.products_to_show,
        title: cmsTitle,
        titleFontColor: contentData?.title_font_color?.color,
        titleFontSize: contentData?.title_font_size,
        titleFontStyle: contentData?.title_font_style,
        titleFontFamily: contentData?.title_font_family,
        titleFontWeight: contentData?.title_font_weight,
        titleMargin: contentData?.title_margin,
        alignTitle: contentData?.align_title,
        padding: contentData?.padding,
    };

    // 6810; PersonalizedHomePageCarouselBuilder
    // name of prop = blockData
    // productsToShow, from CMS personalized_carousel
    // title, from SF, CMS personalized_carousel.heading, CMS title (first field of entry), hardcoded default
    // all title css, from CMS card reference, then CMS service
    // padding, from content.personalized_carousel
    const blockData = {
        productsToShow: personalizedCarouselBlock?.products_to_show || {},
        title: cmsTitle,
        titleMargin: cmsCardRefData?.title_styling?.title_margin,
        titleFontColor: cmsCardRefData?.title_styling?.title_font_color?.color,
        titleFontSize: cmsCardRefData?.title_styling?.title_font_size,
        titleFontStyle: cmsCardRefData?.title_styling?.title_font_style,
        titleFontFamily: cmsCardRefData?.title_styling?.title_font_family,
        titleFontWeight: cmsCardRefData?.title_styling?.title_font_weight || '700',
        alignTitle: cmsCardRefData?.title_styling?.align_title,
        padding: cmsCardRefData?.title_styling?.padding,
        containerPadding: personalizedCarouselBlock?.padding,
        productAlignment: personalizedCarouselBlock?.product_alignment || 'left',
    };

    const trackClickThrough = useClickStreamCustomEventsTracking({
        experimentTracking,
        isFireImpression: false,
        page: { type: 'product' },
    });

    // 6810; tracking func used in grid and carousel components
    const productClickCallbackPLA = (partNumber) => {
        trackClickThrough(partNumber);
        const clickedProduct = products?.find((prod) => prod?.partNumber === partNumber);
        if (trackGA4ProductClick && clickedProduct?.name) {
            dispatch(trackEvent({
                eventName: 'product_click',
                eventCategory: contentData?.ga_tagging?.event_category || 'PLA Recs page',
                eventAction: contentData?.ga_tagging?.event_action || 'Product Click',
                eventLabel: contentData?.ga_tagging?.event_label || decodeHTML(clickedProduct.name),
                products: [
                    {
                        name: decodeHTML(clickedProduct.name),
                        partNumber: clickedProduct.partNumber,
                        price: clickedProduct?.skuPriceRange?.sale?.[0]?.value || clickedProduct?.skuPriceRange?.retail?.[0]?.value || '',
                    },
                ],
            }));
        }
    };

    if (useCardDesign) {
        return (
            <PersonalizedHPCarouselBuilder
                products={products}
                blockData={blockData}
                productClickCallback={productClickCallbackPLA}
                demandType="XS"
                productStyleConfig={cmsCardRefData}
                sfTitle={title}
                isFromSFABTestOnPLA
                emptyArrowPadding
                dataTestId={dataTestId}
                attributeToken={data?.experience?.attributionToken}
            />
        );
    }
    return (
        <SimpleCarouselBuilder
            products={products}
            blockData={scbBlockObj}
            title={title || cmsTitle}
            productClickCallback={productClickCallbackPLA}
            eventTrackingData={{}}
            dataTestId={dataTestId}
            attributeToken={data?.experience?.attributionToken}
        />
    );
};

PLARecsBuilder.propTypes = {
    data: object.isRequired,
    products: arrayOf(shape({
        id: string.isRequired,
        image: shape({
            path: string.isRequired,
            name: string.isRequired,
        }).isRequired,
        name: string.isRequired,
        skuPriceRange: shape({
            retail: arrayOf(shape({
                value: number.isRequired,
            })).isRequired,
            sale: arrayOf(shape({
                value: number.isRequired,
            })).isRequired,
        }).isRequired,
        seo: shape({
            url: string.isRequired,
        }).isRequired,
    })).isRequired,
    contentData: object,
    title: string,
    trackGA4ProductClick: bool,
    dataTestId: string,
    experimentTracking: object,
};

PLARecsBuilder.defaultProps = {
    contentData: {},
    title: '',
    trackGA4ProductClick: false,
    dataTestId: '',
    experimentTracking: {},
};

export default PLARecsBuilder;
